import { routePaths } from 'components/Routes/data';
import { BulkUploadTypes, UploadTypeConfig, UploadTypeConfigCallback } from 'constants/bulkUpload';
import permissions from 'constants/permissions';
import { BulkUploadStagingDetailsSchemaContract } from 'schemas/BulkUploadStagingDetailsSchema';
import { format } from 'date-fns';
import { getRunPayrollMonthList } from 'utils/Dates';

type StagingDataItem = BulkUploadStagingDetailsSchemaContract['bulk_uploads_staging']['data'][0];
type ColumnsArray = NonNullable<UploadTypeConfig['preview']['columns']>;

const getCommonColumns = (): ColumnsArray => [
  {
    title: 'Name',
    render: (item) => item.file_row?.commonProperties?.employeeName,
  },
  {
    title: 'Email',
    render: (item) => item.file_row?.commonProperties?.email,
  },
  {
    title: 'Department',
    render: (item) => item.file_row?.department,
  },
  {
    title: 'Designation',
    render: (item) => item.file_row?.designation,
  },
  {
    title: 'CTC',
    render: (item) => item.file_row?.ctc,
  },
];

const getAllApprovedValueKeys = (
  stagingData?: BulkUploadStagingDetailsSchemaContract['bulk_uploads_staging'],
): string[] => {
  const keys = new Set<string>();
  stagingData?.data?.forEach((item) => {
    const approvedValues = item.file_row?.approvedValues;
    if (approvedValues && typeof approvedValues === 'object' && !Array.isArray(approvedValues)) {
      Object.keys(approvedValues).forEach((k) => keys.add(k));
    }
  });
  return Array.from(keys);
};

const getColumns = (
  stagingData?: BulkUploadStagingDetailsSchemaContract['bulk_uploads_staging'],
): ColumnsArray => {
  const componentKeys = getAllApprovedValueKeys(stagingData);

  return [
    ...getCommonColumns(),
    ...componentKeys.map((componentName) => ({
      title: componentName,
      render: (item: StagingDataItem) => item.file_row?.approvedValues?.[componentName],
    })),
  ];
};

const getCurrentMonthStr = () => {
  const today = new Date();
  today.setDate(1);
  today.setHours(0, 0, 0, 0);
  return format(today, 'yyyy-MM-dd');
};

// getRunPayrollMonthList generates -4 to +7 relative to the given month (12 entries).
const getPayrollMonthOptions = (selectedMonth: string) => {
  const monthList = getRunPayrollMonthList(selectedMonth);
  return monthList.map((month) => ({
    value: format(month, 'yyyy-MM-dd'),
    label: format(month, 'MMMM yyyy'),
  }));
};

export const bulkFbpApprovedValuesConfig: UploadTypeConfigCallback = (data) => {
  const urlParams = new URLSearchParams(data.history.location.search);
  const payrollMonth = urlParams.get('payrollMonth') || '';

  const stagingData = data?.stagingRecordData as
    | BulkUploadStagingDetailsSchemaContract['bulk_uploads_staging']
    | undefined;

  return {
    permissions: [permissions.BULK_ADDITION_DEDUCTION],
    initiation: {
      permissions: [permissions.BULK_ADDITION_DEDUCTION],
      feature: 'bulkFbpApprovedValues',
      pageTitle: 'Bulk FBP Approved Values',
      step1Title: '1. Download the FBP Approved Values template',
      step1Subtitle:
        'Download the template and fill in one approved amount per FBP component for each employee, then upload it here.',
      instructions: [
        'Approved amounts must be greater than 0.',
        'Only components already assigned to the employee can be set.',
        'Do not add or remove any columns.',
        'There is a limit of 500 employees per upload.',
        'Mandatory fields are marked with a * sign.',
      ],
      hasMonthSelector: true,
      skipMonthChangeConfirmation: true,
      dropdownSelectorLabel: 'Select payroll month',
      getDropdownOptions: getPayrollMonthOptions,
      defaultDropdownValue: getCurrentMonthStr(),
      onDropdownOptionChange(selectedMonth) {
        if (selectedMonth) {
          data.history.push({
            pathname: routePaths.bulkUploads.root,
            search: `?type=${BulkUploadTypes.TYPE_BULK_FBP_APPROVED_VALUES}&payrollMonth=${selectedMonth}`,
          });
        }
      },
      onSampleTemplateDownload() {
        window.open(
          `/v2/api/bulk-fbp-approved-values/download-template${payrollMonth ? `?payroll_month=${payrollMonth}` : ''}`,
          '_blank',
        );
      },
    },
    preview: {
      permissions: [permissions.BULK_ADDITION_DEDUCTION],
      feature: 'bulkFbpApprovedValues',
      showColumnsSelector: true,
      columns: getColumns(stagingData),
      renderConfirmationText: () =>
        'Almost done! Please confirm to proceed with the bulk FBP approved values.',
      successModalCtaText: 'Go to Bulk Actions',
      successModalMainText: 'FBP approved values have been applied successfully.',
      successModalSubText: () =>
        'The exempted amounts will be reflected in the next payroll computation.',
      onSuccessModalCtaClick() {
        data.history.push(routePaths.bulkActions);
      },
      onDownloadErrorReport(history) {
        history?.push({
          pathname: routePaths.bulkUploads.initiate,
          search: `type=${BulkUploadTypes.TYPE_BULK_FBP_APPROVED_VALUES}`,
        });
      },
    },
  };
};
